import { GatewayContact } from "@conformite/gateway";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import { UseFormReturn } from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import {
  Form,
  InputButtonGroup,
  InputMask,
  InputText,
  useFormConfig,
  FormField,
  FormRow,
} from "@endya-conformite/ui-kit";
import { recordToSelectOptions } from "@src/adhesion/AdhesionData/adhesionData";
import {
  cleanMaskedInput,
  REGEX_PHONE_AND_MOBILE,
} from "@src/validation.helper";
import { forwardRef, useImperativeHandle } from "react";

export const contactMediationValidationSchema = zod.object({
  nomMediation: zod
    .string({
      errorMap: () => ({
        message: "Veuillez renseigner votre nom",
      }),
    })
    .min(1, { message: "Veuillez renseigner votre nom" }),
  prenomMediation: zod
    .string({
      errorMap: () => ({
        message: "Veuillez renseigner votre prénom",
      }),
    })
    .min(1, { message: "Veuillez renseigner votre prénom" }),
  mailMediation: zod
    .string({
      errorMap: () => ({
        message: "Veuillez renseigner votre email",
      }),
    })
    .email({ message: "Veuillez renseigner un email valide" })
    .min(1, { message: "Veuillez renseigner votre email" }),
  telephoneMediation: zod.preprocess(
    cleanMaskedInput,
    zod
      .string()
      .regex(REGEX_PHONE_AND_MOBILE, {
        message: "Veuillez renseigner votre numéro de téléphone",
      })
      .or(zod.literal(""))
      .optional()
  ),
  civiliteMediation: zod.nativeEnum(GatewayContact.ContactCivilite, {
    errorMap: () => ({
      message: "Veuillez sélectionner votre civilité",
    }),
  }),
});

export type EntrepriseContactMediationFormValues = zod.infer<
  typeof contactMediationValidationSchema
>;
type EntrepriseContactMediationFormProps = {
  onSubmit: (response: EntrepriseContactMediationFormValues) => void;
  formId?: string;
  defaultValues: Partial<EntrepriseContactMediationFormValues>;
};

export type EntrepriseContactMediationFormHandle = {
  getFormConfig: () => UseFormReturn<EntrepriseContactMediationFormValues>;
};

export const civiliteRepresentationRecord: Record<
  GatewayContact.ContactCivilite,
  string
> = {
  [GatewayContact.ContactCivilite.MONSIEUR]: "Monsieur",
  [GatewayContact.ContactCivilite.MADAME]: "Madame",
};

export const civiliteMediationOptions = recordToSelectOptions(
  civiliteRepresentationRecord
);

export const EntrepriseContactMediationForm = forwardRef<
  EntrepriseContactMediationFormHandle,
  EntrepriseContactMediationFormProps
>(({ defaultValues, onSubmit, formId }, ref) => {
  const formConfig = useFormConfig<
    typeof contactMediationValidationSchema,
    EntrepriseContactMediationFormValues
  >({
    schema: contactMediationValidationSchema,
    defaultValues,
  });

  useImperativeHandle(ref, () => ({
    getFormConfig() {
      return formConfig;
    },
  }));

  return (
    <Form formConfig={formConfig} onSubmit={onSubmit} id={formId}>
      <FormRow>
        <FormField>
          <InputButtonGroup<
            EntrepriseContactMediationFormValues,
            false,
            GatewayContact.ContactCivilite
          >
            options={civiliteMediationOptions}
            name="civiliteMediation"
            label="Civilité"
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <InputText<EntrepriseContactMediationFormValues>
            name="nomMediation"
            label="Nom"
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <InputText<EntrepriseContactMediationFormValues>
            name="prenomMediation"
            label="Prénom"
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <InputText<EntrepriseContactMediationFormValues>
            name="mailMediation"
            label="Email"
            required
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField size="half">
          <InputMask
            name="telephoneMediation"
            mask="99 99 99 99 99"
            label="Téléphone"
          />
        </FormField>
      </FormRow>
    </Form>
  );
});
